import React, { useEffect, useState, useRef } from 'react'
import ReactDOM from 'react-dom/client'
import './styles/globals.css'
import {
  UsageLineChart,
  ModelBarChart,
  MaxGauge,
  CostTimeSeriesChart,
  EventsTable,
} from '@puzzle/ui'
import type { Client } from '@libsql/client/web'

interface DashboardData {
  usageData: Array<{ date: string; tokens: number }>
  modelData: Record<string, number>
  maxData: { count: number; limit: number }
  costData: Array<{ date: string; cost: number }>
  eventsData: Array<{
    timestamp: string
    model: string
    tokens: number
    cost: number
    billingType: string
  }>
}

function Dashboard() {
  const [data, setData] = useState<DashboardData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const clientRef = useRef<Client | null>(null)

  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoading(true)
        setError(null)

        // Lazy-initialize the Turso client to avoid top-level errors that prevent React from mounting
        if (!clientRef.current) {
          const { createClient } = await import('@libsql/client/web')
          clientRef.current = createClient({
            url: (import.meta.env.VITE_TURSO_DATABASE_URL || '').trim(),
            authToken: (import.meta.env.VITE_TURSO_AUTH_TOKEN || '').trim(),
          })
        }
        const client = clientRef.current

        // Fetch usage data (daily token usage over last 7 days)
        // Filter out proxy_request events (heartbeats with ~3-10 tokens) to show real work only
        const usageResult = await client.execute(`
          SELECT
            DATE(timestamp) as date,
            SUM(tokens_in + tokens_out) as tokens
          FROM usage_events
          WHERE timestamp >= strftime('%Y-%m-%dT%H:%M:%SZ', 'now', '-7 days')
            AND COALESCE(task_type, '') != 'proxy_request'
          GROUP BY DATE(timestamp)
          ORDER BY date
        `)

        const usageData = usageResult.rows.map((row) => ({
          date: row.date as string,
          tokens: Number(row.tokens),
        }))

        // Fetch model distribution (transform to ModelBreakdown format)
        // Filter out proxy_request events to show real work distribution
        const modelResult = await client.execute(`
          SELECT
            model,
            COUNT(*) as count
          FROM usage_events
          WHERE timestamp >= strftime('%Y-%m-%dT%H:%M:%SZ', 'now', '-7 days')
            AND COALESCE(task_type, '') != 'proxy_request'
          GROUP BY model
          ORDER BY count DESC
        `)

        const modelData: Record<string, number> = {}
        modelResult.rows.forEach((row) => {
          const model = (row.model as string).toLowerCase()
          const count = Number(row.count)
          modelData[model] = count
        })

        // Fetch max messages count (last 5 hours rolling window)
        // Use strftime with ISO 8601 format to match T-separated timestamps in Turso
        const maxResult = await client.execute(`
          SELECT COUNT(*) as count
          FROM max_messages
          WHERE timestamp >= strftime('%Y-%m-%dT%H:%M:%SZ', 'now', '-5 hours')
        `)

        const maxData = {
          count: Number(maxResult.rows[0]?.count || 0),
          limit: 900,
        }

        // Fetch cost data (daily costs over last 7 days)
        // Use strftime with ISO 8601 format to match T-separated timestamps in Turso
        // Filter out proxy_request heartbeats (negligible cost, distorts chart)
        const costResult = await client.execute(`
          SELECT
            DATE(timestamp) as date,
            SUM(cost_usd) as cost
          FROM usage_events
          WHERE timestamp >= strftime('%Y-%m-%dT%H:%M:%SZ', 'now', '-7 days')
            AND billing_type = 'api'
            AND COALESCE(task_type, '') != 'proxy_request'
          GROUP BY DATE(timestamp)
          ORDER BY date
        `)

        const costData = costResult.rows.map((row) => ({
          date: row.date as string,
          cost: Number(row.cost),
        }))

        // Fetch recent events (last 20, excluding proxy heartbeats)
        const eventsResult = await client.execute(`
          SELECT
            timestamp,
            model,
            (tokens_in + tokens_out) as tokens,
            cost_usd as cost,
            billing_type as billingType
          FROM usage_events
          WHERE COALESCE(task_type, '') != 'proxy_request'
          ORDER BY timestamp DESC
          LIMIT 20
        `)

        const eventsData = eventsResult.rows.map((row) => ({
          timestamp: row.timestamp as string,
          model: row.model as string,
          tokens: Number(row.tokens),
          cost: Number(row.cost),
          billingType: (row.billingType as string) || 'unknown',
        }))

        setData({
          usageData,
          modelData,
          maxData,
          costData,
          eventsData,
        })
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err)
        setError(err instanceof Error ? err.message : 'Failed to load data')
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
    // Refresh every 30 seconds
    const interval = setInterval(fetchData, 30000)
    return () => clearInterval(interval)
  }, [])

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
            <h2 className="text-lg font-semibold mb-2">Error Loading Dashboard</h2>
            <p>{error}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <header className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Puzzle Ops Dashboard</h1>
          <p className="text-gray-600 mt-2">Real-time monitoring of agent usage and costs</p>
        </header>

        {/* Top Row: Gauge + Line Chart */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4 text-gray-900">Max Messages (5h)</h2>
            <MaxGauge
              current={data?.maxData.count || 0}
              max={data?.maxData.limit || 900}
              isLoading={isLoading}
              error={error}
            />
          </div>

          <div className="bg-white rounded-lg shadow p-6 lg:col-span-2">
            <h2 className="text-lg font-semibold mb-4 text-gray-900">Daily Token Usage (7d)</h2>
            <UsageLineChart data={data?.usageData || []} isLoading={isLoading} error={error} />
          </div>
        </div>

        {/* Middle Row: Bar Chart + Cost Chart */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4 text-gray-900">Model Distribution (7d)</h2>
            <ModelBarChart data={data?.modelData || {}} isLoading={isLoading} error={error} />
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4 text-gray-900">Daily Costs (7d)</h2>
            <CostTimeSeriesChart data={data?.costData || []} isLoading={isLoading} error={error} />
          </div>
        </div>

        {/* Bottom Row: Events Table */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 text-gray-900">Recent Events</h2>
          <EventsTable data={data?.eventsData || []} isLoading={isLoading} error={error} />
        </div>

        <footer className="mt-8 text-center text-sm text-gray-500">
          Last updated: {new Date().toLocaleTimeString()} • Auto-refresh: 30s
        </footer>
      </div>
    </div>
  )
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Dashboard />
  </React.StrictMode>
)
